// The assistant conversation (spec §3.8, §9 C6/C10): history, the fixed composer, Stop, replies and their actions.
// One UI for the panel and (Task 12) the Home/project chats. Replies arrive by `assistant.reply.ready` in project
// sessions and by polling on Home (or whenever the event stream is not live). Auto actions run only for a turn this
// tab started, right after its reply arrives — never from history — and each is claimed on the server first.
// D34 (operator loop, operator-loop.ts): after a reply's auto actions ran, an automatic follow-up turn carries their
// outcomes back to the assistant (at most ASSISTANT_MAX_FOLLOW_UPS per question, Stop at any time, a confirm card
// pauses the loop until it is clicked, a failed action is never repeated by itself).
// D36: the shared Composer (attachments, dictation, slash commands, counter); the Home chat adds its controls (mode,
// agent, integrations) through `composerContext` + `controls`, which travel in every turn's context, follow-ups included.
import { Fragment, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import type { AssistantAttachmentRef } from "@maestros/contracts";
import { isApiError } from "../../api/client.ts";
import { cancelAssistantTurn, listAssistantMessages, postAssistantTurn } from "../../api/endpoints-assistant.ts";
import type { AssistantAction, AssistantClaimVia, AssistantContext, AssistantFocus, AssistantMessage, AssistantTurnAccepted, AssistantTurnRequest } from "../../api/types-assistant.ts";
import { useI18n, type Locale } from "../../i18n/index.tsx";
import { useRealtime, useRealtimeEvents } from "../../realtime/RealtimeProvider.tsx";
import { recoveredFromGap } from "../../realtime/resync.ts";
import { newId, readLocal, removeLocal, writeLocal } from "../../lib/storage.ts";
import { ActionError } from "../../components/ActionError.tsx";
import { Button, IconButton } from "../../components/Button.tsx";
import { Banner, ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { apiErrorView } from "../../lib/apiErrors.ts";
import { LoaderIcon, SendIcon } from "../../components/icons.tsx";
import { Composer } from "../composer/Composer.tsx";
import { AttachmentChips } from "../composer/AttachmentChips.tsx";
import { attachmentRefs, useComposerAttachments } from "../composer/attachments.ts";
import { HelpPanel, McpPanel, NotePanel } from "../composer/ComposerPanels.tsx";
import { ALL_SLASH_COMMANDS, parseSlash, type SlashCommand } from "../composer/slash.ts";
import { autoRunnable, claimAndRun, projectIdOf, type ActionResult } from "./actions.ts";
import { actionSignature, newLoop, nextStep, planAutoRun, runInOrder, serverOwnsFollowUp, waitingForClick, type OperatorLoop } from "./operator-loop.ts";
import { AssistantMessageView, replySpeech, type LocalOutcome } from "./AssistantMessage.tsx";
import { BackgroundWork } from "./BackgroundWork.tsx";
import type { AssistantRequest } from "./AssistantProvider.tsx";

export const MAX_CHARS = 4000;
const POLL_MS = 1500;
const POLL_LIMIT_MS = 90_000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export { projectIdOf };

/** Ids, the route and the UI locale only, never page text (§6; D34: the locale picks the answer's language). */
function contextOf(pathname: string, search: string, focus: AssistantFocus, locale: Locale): AssistantContext {
  const task = new URLSearchParams(search).get("task");
  return { route: pathname.slice(0, 200), project_id: projectIdOf(pathname), task_id: task !== null && UUID.test(task) ? task : null, focus, locale };
}

function merge(current: AssistantMessage[], incoming: AssistantMessage[]): AssistantMessage[] {
  const byId = new Map(current.map((m) => [m.id, m]));
  for (const m of incoming) {
    const previous = byId.get(m.id);
    if (previous?.turn && m.turn) {
      const closed = previous.turn.state !== "pending" && previous.turn.state !== "running";
      const open = m.turn.state === "pending" || m.turn.state === "running";
      if (closed && open) continue;
      if (open && (previous.turn.progress?.revision ?? 0) > (m.turn.progress?.revision ?? 0)) continue;
    }
    byId.set(m.id, m);
  }
  return [...byId.values()].sort((a, b) => a.sequence - b.sequence);
}

/**
 * Whether `pending`'s reply is already among `messages` — true once either `loadHistory()` or `refresh()` has
 * seen it, whichever wins the mount race (both run concurrently: the panel's `request` effect calls `send()`,
 * which awaits its own `refresh()`, while the mount effect's `loadHistory()` fetches the same session at the same
 * time). Checked on both paths so neither one's "nothing new here" outcome leaves the other's pending turn stuck.
 */
function pendingSettled(messages: AssistantMessage[], pending: Pending | null): boolean {
  return pending !== null && messages.some((m) => m.reply?.turn_id === pending.turnId);
}

/** The newest question still being answered (from any tab), if any. */
function activeTurnOf(messages: AssistantMessage[]): string | null {
  const answered = new Set(messages.map((m) => m.reply?.turn_id).filter(Boolean));
  const open = messages.filter((m) => m.turn && (m.turn.state === "pending" || m.turn.state === "running") && !answered.has(m.id));
  return open.at(-1)?.id ?? null;
}

interface Pending {
  turnId: string;
  progressRevision?: number;
  since: number;
  stalled: boolean;
  stopping: boolean;
}

/** D36: what the Home chat's controls add to every turn's context (absent fields are not sent). */
export type ComposerContext = Pick<AssistantContext, "mode" | "agent_profile_id" | "integration_ids">;

function withComposerContext(context: AssistantContext, extra: ComposerContext | undefined): AssistantContext {
  if (!extra) return context;
  return {
    ...context,
    ...(extra.mode ? { mode: extra.mode } : {}),
    ...(extra.agent_profile_id ? { agent_profile_id: extra.agent_profile_id } : {}),
    ...(extra.integration_ids && extra.integration_ids.length > 0 ? { integration_ids: extra.integration_ids } : {}),
  };
}

type ComposerPanel = { kind: "help" } | { kind: "mcp" } | { kind: "note"; text: string } | null;

export function AssistantConversation({
  projectId,
  sessionId: fixedSessionId,
  compact = false,
  request = null,
  onRequestHandled,
  composerContext,
  controls,
  onPickControl,
  placeholder,
  onConversationActive,
  onFirstExchange,
  onSessionChanged,
}: {
  projectId: string | null;
  sessionId?: string;
  compact?: boolean;
  request?: AssistantRequest | null;
  onRequestHandled?: () => void;
  /** D36: mode / agent / integrations chosen in the Home chat's controls. */
  composerContext?: ComposerContext;
  /** D36: the Home chat's selectors, shown in the composer's toolbar. */
  controls?: ReactNode;
  /** D36: `/agent` and `/mode` focus the matching control; false (or absent) ⇒ a note that they live on Home. */
  onPickControl?: (which: "agent" | "mode") => boolean;
  placeholder?: string;
  /** Home Ask: true once this tab's question was accepted, false after `/clear` (Home switches its view). */
  onConversationActive?: (active: boolean) => void;
  /** Home Ask: the first answer arrived; gets the first question's text ("" when it cannot be read) — once per conversation. Only `/clear` reports null. */
  onFirstExchange?: (question: string | null) => void;
  onSessionChanged?: (id: string | null) => void;
}) {
  const { t, locale } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const storageKey = `assistant.session.${projectId ?? "home"}`;
  const composerId = useId();

  const [sessionId, setSessionId] = useState<string | null>(() => fixedSessionId ?? readLocal<string | null>(storageKey, null));
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [history, setHistory] = useState<"loading" | "ready" | { error: unknown }>(sessionId ? "loading" : "ready");
  const [pending, setPending] = useState<Pending | null>(null);
  const [text, setText] = useState("");
  /** The question being sent; `turnId` once accepted (the bubble stays until the stored question is read). */
  const [posting, setPosting] = useState<{ text: string; turnId: string | null; attachments: AssistantAttachmentRef[] } | null>(null);
  /** D36: a panel a slash command opened above the composer (help, integrations, a note). */
  const [panel, setPanel] = useState<ComposerPanel>(null);
  const attachments = useComposerAttachments(t, projectId);
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  const composerContextRef = useRef(composerContext);
  composerContextRef.current = composerContext;
  const conversationActiveRef = useRef(onConversationActive);
  conversationActiveRef.current = onConversationActive;
  const firstExchangeRef = useRef(onFirstExchange);
  firstExchangeRef.current = onFirstExchange;
  const sessionChangedRef = useRef(onSessionChanged);
  sessionChangedRef.current = onSessionChanged;
  /** The first exchange was already reported for the current conversation. */
  const exchangeReported = useRef(false);
  const [error, setError] = useState<unknown>(null);
  const [outcomes, setOutcomes] = useState<Record<string, LocalOutcome>>({});
  const [focus, setFocus] = useState<AssistantFocus>("general");
  /** The newest reply that arrived while open, for screen readers (history loaded on open is never announced). */
  const [announcement, setAnnouncement] = useState("");

  const sessionRef = useRef(sessionId);
  const messagesRef = useRef(messages);
  const pendingRef = useRef(pending);
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);
  const inFlight = useRef(false);
  const startedActions = useRef(new Set<string>());
  const settledActions = useRef(new Set<string>());
  const followedReplies = useRef(new Set<string>());
  /**
   * Turns started by THIS conversation (C6): only these may run auto actions, once, when their reply arrives. Per
   * instance, never module-wide: a conversation remounted later (back to project A from B) never auto-runs a turn an
   * earlier instance asked — its reply shows as cards.
   */
  const startedTurns = useRef(new Set<string>());
  const handledRequest = useRef<AssistantRequest | null>(null);
  /** The last question that did not get through: resending the same text replays its key and body (idempotent). */
  const retry = useRef<{ text: string; key: string; body: AssistantTurnRequest } | null>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  /** D34: the current question's operator loop (per instance, like startedTurns); replaced by every new user question. */
  const loopRef = useRef<OperatorLoop>(newLoop());
  /** The reply whose actions the loop is on: a click on one of its confirm cards resumes the loop. */
  const loopReplyRef = useRef<string | null>(null);
  /** Cards skipped with "Not now" (they no longer hold the loop). */
  const dismissedRef = useRef(new Set<string>());
  /** Auto actions are running or a follow-up is about to be sent: the composer shows Stop. */
  const [looping, setLooping] = useState(false);
  messagesRef.current = messages;
  pendingRef.current = pending;
  // The first answer of the conversation (live or loaded from the owner's own history) reports its question once.
  useEffect(() => {
    if (exchangeReported.current) return;
    const answered = messages.find((m) => m.reply);
    if (!answered?.reply) return;
    exchangeReported.current = true;
    const turnId = answered.reply.turn_id;
    const question = messages.find((m) => m.id === turnId);
    // An unreadable question is "" (never null, which means "cleared"), so Home shows the fallback title (AC-3).
    firstExchangeRef.current?.(typeof question?.content === "string" ? question.content : "");
  }, [messages]);
  navigateRef.current = navigate;
  locationRef.current = location;

  const updatePending = useCallback((next: Pending | null) => {
    pendingRef.current = next;
    setPending(next);
  }, []);

  const rememberSession = useCallback(
    (id: string | null) => {
      sessionRef.current = id;
      setSessionId(id);
      if (id) writeLocal(storageKey, id);
      else removeLocal(storageKey);
      sessionChangedRef.current?.(id);
      window.dispatchEvent(new Event("maestros:assistant-history-changed"));
    },
    [fixedSessionId, storageKey],
  );

  /** Keeps keyboard focus in the panel when the pressed control goes away (chip, card, Stop, banner). */
  const focusComposer = useCallback(() => composerRef.current?.focus(), []);

  const setOutcome = useCallback((key: string, outcome: LocalOutcome) => setOutcomes((prev) => ({ ...prev, [key]: outcome })), []);
  const handedOffReplies = useRef(new Set<string>());

  /** Claim → run → report one action; null when this tab already started it, "already_claimed" when another did. */
  const runOne = useCallback(
    async (messageId: string, index: number, action: AssistantAction, via: AssistantClaimVia): Promise<ActionResult | "already_claimed" | null> => {
      const key = `${messageId}:${index}`;
      // A double click (or an auto run racing a click) reaches the server once from this tab.
      if (startedActions.current.has(key)) return null;
      startedActions.current.add(key);
      setOutcome(key, { state: "running" });
      const result = await claimAndRun(messageId, index, action, { navigate: (to) => navigateRef.current(to), currentPath: locationRef.current.pathname }, via);
      if (result !== "already_claimed" && result.outcome === "succeeded" && result.refs?.length && ["create_task", "create_system_task", "create_project_request"].includes(action.type)) handedOffReplies.current.add(messageId);
      if (result !== "already_claimed") settledActions.current.add(key);
      setOutcome(key, result === "already_claimed" ? { state: "elsewhere" } : { state: "done", result });
      return result;
    },
    [setOutcome],
  );

  // The follow-up sender is defined after send(); the loop reaches it through this ref.
  const followUpRef = useRef<(replyMessageId: string, round: number, loop: OperatorLoop) => Promise<void>>(async () => undefined);

  /**
   * D34: after the actions of `message` ran (`ran` attempted now), continue with an automatic follow-up turn or stop.
   * A loop replaced meanwhile (the user asked something else) is left alone.
   */
  const continueLoop = useCallback(async (message: AssistantMessage, loop: OperatorLoop, ran: number) => {
    if (loop !== loopRef.current) return;
    const reply = message.reply;
    if (!reply) return;
    // D41-B2: the server executor sends this reply's follow-up (its turn is server-run and has a server-set action).
    if (serverOwnsFollowUp(reply, messagesRef.current.find((m) => m.id === reply.turn_id)?.turn, projectId === null)) {
      setLooping(false);
      return;
    }
    if (handedOffReplies.current.has(message.id)) {
      setLooping(false);
      return;
    }
    if (followedReplies.current.has(message.id) || inFlight.current) return;
    // Starting another confirmation is not proof that its effect and claim report have settled.
    if (reply.actions.some((_action, i) => startedActions.current.has(`${message.id}:${i}`) && !settledActions.current.has(`${message.id}:${i}`))) {
      setLooping(false);
      return;
    }
    // Queued operations can hold maintenance admission until their durable receipt settles.
    if (ran > 0 && reply.actions.length > 0 && (reply.actions.some(action => action.type === "server_operation") || reply.actions.every(action => action.type === "create_project_request" || action.type === "publish_project"))) {
      setLooping(false);
      return;
    }
    const settled = (i: number) => settledActions.current.has(`${message.id}:${i}`) || dismissedRef.current.has(`${message.id}:${i}`);
    const decision = nextStep(loop, { ran, waitingForClick: waitingForClick(reply, settled) });
    if (decision.kind === "stop") {
      setLooping(false);
      return;
    }
    loop.rounds = decision.round;
    followedReplies.current.add(message.id);
    await followUpRef.current(message.id, decision.round, loop);
  }, [projectId]);

  /** Right after a reply to a turn this tab started: claim and run its unclaimed auto actions, in order, then continue the loop. */
  const runAutoActions = useCallback(
    async (message: AssistantMessage) => {
      const reply = message.reply;
      if (!reply) return;
      const loop = loopRef.current;
      loopReplyRef.current = message.id;
      const turn = messagesRef.current.find((m) => m.id === reply.turn_id)?.turn;
      const turnRoute = turn?.context.route ?? null;
      // Read the live location: an earlier action of this reply may have navigated already.
      const runnable = (action: AssistantAction) => autoRunnable(action, { sessionProjectId: projectId, currentPath: window.location.pathname, turnRoute, turn });
      const plan = planAutoRun(reply, loop, runnable);
      if (plan.run.length > 0) setLooping(true);
      const ran = await runInOrder(reply, plan, loop, async (index, action) => {
        if (!runnable(action)) return "skipped";
        const result = await runOne(message.id, index, action, "auto");
        return result === null || result === "already_claimed" ? "skipped" : result;
      });
      await continueLoop(message, loop, ran);
    },
    [continueLoop, projectId, runOne],
  );

  /** Reads what is new in the session; a reply to the waiting turn ends the wait and may run its auto actions. */
  const refresh = useCallback(async (full = false) => {
    const sid = sessionRef.current;
    if (!sid) return;
    const lastSequence = messagesRef.current.reduce((n, m) => Math.max(n, m.sequence), 0);
    const active = messagesRef.current.find((m) => m.id === pendingRef.current?.turnId);
    // Progress updates the existing question's metadata; an exclusive cursor past it would lose every delta.
    const after = active ? Math.min(lastSequence, active.sequence - 1) : lastSequence;
    let items: AssistantMessage[];
    try {
      // `full` (E2.9, after a reconnect): re-read the whole session so a turn changed during the gap is not missed.
      items = (await listAssistantMessages(sid, full ? undefined : after)).items;
    } catch {
      return; // transient: the next poll or event reads again
    }
    if (sessionRef.current !== sid) return;
    const known = new Set(messagesRef.current.map((m) => m.id));
    const fresh = items.filter((m) => !known.has(m.id));
    if (items.length > 0) {
      messagesRef.current = merge(messagesRef.current, items);
      setMessages(messagesRef.current);
      const newest = fresh.filter((m) => m.reply).at(-1);
      if (newest?.reply) setAnnouncement(replySpeech(t, newest.reply, newest.content));
    }
    // Fix round 1 (Important 1): scan every known message, not only what this call found "fresh" — the concurrent
    // mount-time loadHistory() may have already merged in the reply (mount race), in which case it never shows up
    // as "fresh" here. startedTurns.delete() is still the single-execution latch (only the conversation that asked
    // holds an entry for it), so re-scanning already-handled messages on later polls is a harmless no-op.
    for (const m of messagesRef.current) if (m.reply && startedTurns.current.delete(m.reply.turn_id)) void runAutoActions(m);
    const currentTurn = messagesRef.current.find(m => m.id === pendingRef.current?.turnId)?.turn;
    if (pendingSettled(messagesRef.current, pendingRef.current) || (currentTurn && currentTurn.state !== "pending" && currentTurn.state !== "running")) updatePending(null);
    else if (currentTurn?.progress && pendingRef.current && currentTurn.progress.revision > (pendingRef.current.progressRevision ?? -1)) {
      updatePending({ ...pendingRef.current, since: Date.now(), progressRevision: currentTurn.progress.revision, stalled: false });
    }
    // E2.9: the server decides whether a question is still being answered, not what this tab remembers.
    if (full && !pendingRef.current) {
      const open = activeTurnOf(messagesRef.current);
      if (open) updatePending({ turnId: open, since: Date.now(), stalled: false, stopping: false });
    }
  }, [runAutoActions, t, updatePending]);

  // E2.9: when the event stream recovers from a gap, rebuild the conversation state from the server.
  const { status: streamStatus } = useRealtime();
  const previousStream = useRef(streamStatus);
  useEffect(() => {
    const previous = previousStream.current;
    previousStream.current = streamStatus;
    if (recoveredFromGap(previous, streamStatus)) void refresh(true);
  }, [streamStatus, refresh]);

  // History: loaded once; never runs an action (C6). A session that is gone (404) starts afresh with the next question.
  const loadHistory = useCallback(
    async (signal?: AbortSignal) => {
      const sid = sessionRef.current;
      if (!sid) return;
      setHistory("loading");
      try {
        const res = await listAssistantMessages(sid, undefined, signal);
        if (signal?.aborted || sessionRef.current !== sid) return;
        rememberSession(sid);
        messagesRef.current = merge(res.items, messagesRef.current);
        setMessages(messagesRef.current);
        conversationActiveRef.current?.(messagesRef.current.some(m => m.author_role === "user"));
        // The concurrent send() (from a `request` prompt firing on the same mount) may already have posted a turn
        // and be awaiting its own refresh(): settle it here too if its reply is already in this snapshot.
        if (pendingSettled(messagesRef.current, pendingRef.current)) updatePending(null);
        const active = activeTurnOf(messagesRef.current);
        if (active && !pendingRef.current) updatePending({ turnId: active, since: Date.now(), stalled: false, stopping: false });
        setHistory("ready");
      } catch (e) {
        if (signal?.aborted) return;
        if (isApiError(e) && e.status === 404) {
          rememberSession(null);
          setHistory("ready");
        } else setHistory({ error: e });
      }
    },
    [rememberSession, updatePending],
  );

  // The conversation remounts when the panel follows a navigation to another scope: focus that was in the old
  // composer (now gone, so on <body>) comes back here. An embedded conversation never takes focus by itself.
  useEffect(() => {
    if (!compact && (document.activeElement === null || document.activeElement === document.body)) focusComposer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadHistory(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = useCallback(
    async (raw: string, focusOverride?: AssistantFocus, withAttachments = false) => {
      // D36: the composer's ready attachments go with the user's own question (never with a chip or a prompt).
      const files = withAttachments ? attachmentRefs(attachmentsRef.current.items) : [];
      const question = raw.trim() || (files.length > 0 ? t("composer.attachmentsOnly") : "");
      // A stalled wait (90 s) no longer blocks a new question: the server answers 409 if that turn is still active.
      if (!question || question.length > MAX_CHARS || inFlight.current || (pendingRef.current && !pendingRef.current.stalled)) return;
      if (withAttachments && attachmentsRef.current.uploading) {
        setPanel({ kind: "note", text: t("composer.waitUploads") });
        return;
      }
      inFlight.current = true;
      setPosting({ text: question, turnId: null, attachments: files });
      setError(null);
      setText("");
      setPanel(null);
      focusComposer();
      // The same text (and files) again after a failure reuses the failed request's key and body, so a request that did
      // reach the server replays its turn instead of opening another one (the server rolls a refused key back, so reuse
      // is safe).
      const filesKey = files.map((f) => f.id).join(",");
      let attempt = retry.current?.text === question && (retry.current.body.attachments ?? []).join(",") === filesKey ? retry.current : null;
      if (!attempt) {
        const loc = locationRef.current;
        const sid = sessionRef.current;
        const context = withComposerContext(contextOf(loc.pathname, loc.search, focusOverride ?? focus, locale), composerContextRef.current);
        const body: AssistantTurnRequest = { project_id: projectId, text: question, context, ...(files.length > 0 ? { attachments: files.map((f) => f.id) } : {}) };
        attempt = { text: question, key: newId(), body: sid ? { ...body, session_id: sid } : body };
      }
      retry.current = attempt;
      try {
        let accepted: AssistantTurnAccepted;
        try {
          accepted = await postAssistantTurn(attempt.body, attempt.key);
        } catch (e) {
          // R17: a remembered session that is gone starts a fresh one (a different body, so a new key).
          if (!(attempt.body.session_id && isApiError(e) && e.status === 404)) throw e;
          rememberSession(null);
          const { session_id: _gone, ...fresh } = attempt.body;
          retry.current = { text: question, key: newId(), body: fresh };
          accepted = await postAssistantTurn(retry.current.body, retry.current.key);
        }
        retry.current = null;
        // A new question of the user's own starts a new operator loop (D34): the previous one ends here.
        loopRef.current.stopped = true;
        loopRef.current = newLoop();
        setLooping(false);
        startedTurns.current.add(accepted.turn_id);
        if (accepted.session_id !== sessionRef.current) rememberSession(accepted.session_id);
        conversationActiveRef.current?.(true);
        setFocus("general");
        updatePending({ turnId: accepted.turn_id, since: Date.now(), stalled: false, stopping: false });
        setPosting({ text: question, turnId: accepted.turn_id, attachments: files });
        if (files.length > 0) attachmentsRef.current.clear();
        await refresh();
      } catch (e) {
        setError(e);
        setText((current) => current || raw.trim());
        // Another tab's question is being answered in this session: show it and wait for it (no auto actions here).
        if (isApiError(e) && e.status === 409) {
          await refresh();
          const active = activeTurnOf(messagesRef.current);
          if (active && (!pendingRef.current || pendingRef.current.stalled)) updatePending({ turnId: active, since: Date.now(), stalled: false, stopping: false });
        }
      } finally {
        inFlight.current = false;
        setPosting(null);
      }
    },
    [focus, focusComposer, locale, projectId, refresh, rememberSession, t, updatePending],
  );

  /**
   * D34: the automatic follow-up turn (fixed text in the UI language, never shown as a question bubble). The server reads
   * the outcomes from the reply's claim ledger and checks the round continues that reply's chain. A refusal ends the loop.
   */
  const sendFollowUp = useCallback(
    async (replyMessageId: string, round: number, loop: OperatorLoop) => {
      const sid = sessionRef.current;
      if (!sid || loop !== loopRef.current || loop.stopped) {
        setLooping(false);
        return;
      }
      const loc = locationRef.current;
      // D36: a follow-up keeps the question's mode, agent and integration hints (the mode is enforced per turn).
      const context: AssistantContext = { ...withComposerContext(contextOf(loc.pathname, loc.search, "general", locale), composerContextRef.current), follow_up: { reply_message_id: replyMessageId, round } };
      const body: AssistantTurnRequest = { session_id: sid, project_id: projectId, text: t("assistant.followUp.prompt"), context };
      try {
        const accepted = await postAssistantTurn(body, newId());
        if (loop !== loopRef.current || loop.stopped) return;
        startedTurns.current.add(accepted.turn_id);
        updatePending({ turnId: accepted.turn_id, since: Date.now(), stalled: false, stopping: false });
        setLooping(false);
        await refresh();
      } catch (e) {
        setError(e);
        setLooping(false);
      }
    },
    [locale, projectId, refresh, t, updatePending],
  );
  followUpRef.current = sendFollowUp;

  // `open({ prompt, focus })` from anywhere: taken once.
  useEffect(() => {
    if (!request || handledRequest.current === request) return;
    handledRequest.current = request;
    onRequestHandled?.();
    setFocus(request.focus);
    if (request.prompt) void send(request.prompt, request.focus);
  }, [request, onRequestHandled, send]);

  // Poll durable snapshots even with live SSE, so a missed notification cannot strand a draft or Stop.
  useEffect(() => {
    if (!pending || pending.stalled) return;
    const id = window.setInterval(() => {
      const p = pendingRef.current;
      if (!p) return;
      if (Date.now() - p.since > POLL_LIMIT_MS) updatePending({ ...p, stalled: true });
      else void refresh();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [pending, refresh, updatePending]);

  useRealtimeEvents((e) => {
    if ((e.type === "assistant.reply.ready" || e.type === "assistant.turn.progress") && e.payload["session_id"] === sessionRef.current) void refresh();
    // D41-B2: the server settled a claim of a reply shown here (this event carries no session id). A full read: an
    // incremental one starts after the newest message and never returns an older reply.
    if (e.type === "assistant.action.executed" && messagesRef.current.some((m) => m.id === e.payload["message_id"])) void refresh(true);
  }, projectId);

  const stop = async () => {
    // D34: Stop also ends the operator loop — no further auto action, no further follow-up.
    loopRef.current.stopped = true;
    setLooping(false);
    const p = pendingRef.current;
    if (!p || p.stopping) {
      focusComposer();
      return;
    }
    focusComposer();
    updatePending({ ...p, stopping: true });
    try {
      await cancelAssistantTurn(p.turnId);
    } catch (e) {
      setError(e);
      if (pendingRef.current) updatePending({ ...pendingRef.current, stopping: false });
      return;
    }
    await refresh();
  };

  const checkAgain = () => {
    const p = pendingRef.current;
    if (p) updatePending({ ...p, since: Date.now(), stalled: false });
    focusComposer();
    void refresh();
  };

  const closePanel = () => {
    setPanel(null);
    focusComposer();
  };

  /** D36 `/clear`: a new conversation (the next question opens a new session; the old one stays on the server). */
  const newConversation = () => {
    loopRef.current.stopped = true;
    loopRef.current = newLoop();
    setLooping(false);
    rememberSession(null);
    exchangeReported.current = false;
    firstExchangeRef.current?.(null);
    messagesRef.current = [];
    setMessages([]);
    updatePending(null);
    setOutcomes({});
    setError(null);
    setHistory("ready");
    retry.current = null;
    attachmentsRef.current.clear();
    conversationActiveRef.current?.(false);
    setPanel({ kind: "note", text: t("composer.newConversation") });
    focusComposer();
  };

  /** D36 slash commands: existing actions, local panels, or a prefilled / sent prompt. */
  const runCommand = (command: SlashCommand, rest: string) => {
    const prompt = (key: "composer.prompt.task" | "composer.prompt.plan" | "composer.prompt.fix", prefill: "composer.prefill.task" | "composer.prefill.plan" | "composer.prefill.fix") => {
      if (rest) void send(t(key, { text: rest }), undefined, true);
      else {
        setText(t(prefill));
        focusComposer();
      }
    };
    switch (command.name) {
      case "clear":
        return newConversation();
      case "help":
        return setPanel({ kind: "help" });
      case "mcp":
        return setPanel({ kind: "mcp" });
      case "agent":
      case "mode":
        if (!onPickControl?.(command.name)) setPanel({ kind: "note", text: t("composer.slash.homeOnly") });
        return;
      case "task":
        return prompt("composer.prompt.task", "composer.prefill.task");
      case "plan":
        return prompt("composer.prompt.plan", "composer.prefill.plan");
      case "fix":
        return prompt("composer.prompt.fix", "composer.prefill.fix");
      case "status":
        return void send(t("composer.prompt.status"), "readiness");
      case "validate":
        return void send(t("composer.prompt.validate"), "readiness");
      case "publish":
        return void send(t("composer.prompt.publish"));
      case "logs":
        return void send(t("composer.prompt.logs"), "runs");
      case "guardian":
        return void send(t("composer.prompt.guardian"));
    }
  };

  /** Enter or the send button: a known `/command` runs; anything else (unknown commands included) is sent as text. */
  const submit = () => {
    const parsed = parseSlash(text, ALL_SLASH_COMMANDS);
    if (parsed) {
      setText("");
      runCommand(parsed.command, parsed.rest);
      return;
    }
    void send(text, undefined, true);
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit();
  };

  const tooLong = text.length > MAX_CHARS;
  const waiting = pending !== null && !pending.stalled;
  const progress = pending && !pending.stopping ? messages.find(m => m.id === pending.turnId)?.turn?.progress : undefined;
  const busy = posting !== null || waiting || looping;
  const canSend = (text.trim().length > 0 || attachments.ids.length > 0) && !tooLong && !busy && !attachments.uploading;
  const lastId = messages.at(-1)?.id;
  // D36: a reply shows the files its question carried.
  const turnFiles = new Map(messages.flatMap((m) => (m.turn?.attachments?.length ? [[m.id, m.turn.attachments] as const] : [])));
  // Auto-scroll: follow new messages while the reader is at (or near) the end of the log; someone who scrolled up to
  // re-read is left where they are. Sending a question always brings the end into view.
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToEnd = useRef(true);
  const onLogScroll = () => {
    const el = scrollRef.current;
    if (el) stickToEnd.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };
  if (posting !== null) stickToEnd.current = true;
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && stickToEnd.current) el.scrollTop = el.scrollHeight;
  }, [messages.length, lastId, posting, pending, history, outcomes]);
  const turnActive = isApiError(error) && error.status === 409 && error.details?.["cause"] === "turn_active";

  return (
    <div className={`assistant-conversation${compact ? " assistant-conversation--compact" : ""}`}>
      <div className="assistant-scroll" ref={scrollRef} onScroll={onLogScroll} data-testid="assistant-scroll">
        {history === "loading" ? <LoadingBlock label={t("common.loading")} lines={2} /> : null}
        {typeof history === "object" ? <ErrorBanner
            error={history.error}
            title={t("assistant.historyFailed")}
            onRetry={() => {
              focusComposer();
              void loadHistory();
            }}
          /> : null}
        {history === "ready" && messages.length === 0 && posting === null ? <p className="assistant-empty subtle">{t("assistant.empty")}</p> : null}
        {/* Only a reply that arrives while open is announced (Task 11 minor): the log itself is not a live region, so
            opening the panel on an existing conversation never reads the whole history aloud. */}
        <p className="sr-only" role="status" aria-live="polite" data-testid="assistant-announcer">
          {announcement}
        </p>
        <ol className="chat-log assistant-log" data-testid="assistant-log" aria-label={t("assistant.log")}>
          {messages.map((m) => {
            const localRefs = m.reply?.actions.flatMap((_action, index) => {
              const local = outcomes[`${m.id}:${index}`];
              return local?.state === "done" ? local.result.refs ?? [] : [];
            }) ?? [];
            const refs = [...new Map([...(m.reply?.refs ?? []), ...localRefs]
              .filter(ref => ["task", "prd_version", "planning_run"].includes(ref.type) && UUID.test(ref.id) && /^\/projects\/[0-9a-f-]{36}\/(kanban|prds|runs)$/i.test(ref.href))
              .map(ref => [`${ref.type}:${ref.id}`, ref])).values()];
            return <Fragment key={m.id}>
            <AssistantMessageView
              message={m}
              answeredTurn={m.reply ? messages.find(turn => turn.id === m.reply?.turn_id)?.turn ?? undefined : undefined}
              sessionProjectId={projectId}
              outcomes={outcomes}
              isLast={m.id === lastId}
              busy={busy}
              answeredAttachments={m.reply ? turnFiles.get(m.reply.turn_id) : undefined}
              onRun={(index) => {
                const action = m.reply?.actions[index];
                focusComposer();
                if (!action) return;
                // D34: clicking a confirm card of the reply the loop paused on resumes the loop once it ran.
                const loop = loopRef.current;
                const resumes = loopReplyRef.current === m.id && !loop.stopped;
                void runOne(m.id, index, action, "click").then((result) => {
                  if (resumes && result !== null && result !== "already_claimed") {
                    if (result.outcome === "failed") loop.failed.add(actionSignature(action));
                    void continueLoop(m, loop, 1);
                  }
                });
              }}
              onDismiss={(index) => {
                focusComposer();
                dismissedRef.current.add(`${m.id}:${index}`);
                setOutcome(`${m.id}:${index}`, { state: "dismissed" });
              }}
              onSuggestion={(s) => void send(s)}
            />
            {refs.length ? <li className="assistant-note" data-testid="assistant-handoff-references"><ul>{refs.map((ref, index) => <BackgroundWork key={`${ref.type}:${ref.id}`} reference={ref} live={index < 4 && !messages.slice(messages.indexOf(m) + 1).some(next => next.reply?.refs.some(item => ["task", "planning_run"].includes(item.type)))} />)}</ul></li> : null}
            </Fragment>;
          })}
          {posting !== null && !messages.some((m) => m.id === posting.turnId) ? (
            <li className="chat-msg chat-msg--user">
              <div className="chat-bubble">
                <p className="user-content assistant-text">{posting.text}</p>
                <AttachmentChips items={posting.attachments.map((a) => ({ key: a.id, name: a.name, size: a.size_bytes, kind: a.kind, mime: a.mime, attachmentId: a.id }))} testId="assistant-posting-attachments" />
              </div>
            </li>
          ) : null}
          {progress?.phase === "answer" && progress.text ? (
            <li className="chat-msg chat-msg--assistant" data-testid="assistant-streaming-reply">
              <div className="chat-bubble"><p className="assistant-text">{progress.text}</p></div>
            </li>
          ) : null}
          {pending ? (
            <li className="assistant-thinking" role="status">
              {pending.stalled ? (
                <>
                  <span>{t("assistant.stillWorking")}</span>
                  <Button size="sm" onClick={checkAgain}>
                    {t("assistant.checkAgain")}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void stop()} disabled={pending.stopping}>
                    {pending.stopping ? t("assistant.stopping") : t("assistant.stop")}
                  </Button>
                </>
              ) : (
                <>
                  <LoaderIcon size={16} className="assistant-spin" />
                  <span>{pending.stopping ? t("assistant.stopping") : progress ? t(`assistant.progress.${progress.phase}`) : t("assistant.thinking")}</span>
                </>
              )}
            </li>
          ) : null}
        </ol>
      </div>

      {/* Fix round 1 (Important 3): a fixed chip, not tied to an empty log — the session persists per project and
          there is no "new conversation" control, so it must stay reachable for the whole conversation, only
          hidden while a question is in flight (matching the last reply's own suggestion chips). */}
      {projectId !== null && !busy ? (
        <ul className="assistant-chips assistant-chips--fixed" data-testid="assistant-summary-chip">
          <li>
            <button type="button" className="assistant-chip" onClick={() => void send(t("assistant.prompt.summary"), "summary")}>
              {t("assistant.summaryChip")}
            </button>
          </li>
        </ul>
      ) : null}

      <form className="assistant-composer" onSubmit={onSubmit}>
        {error ? (
          turnActive ? (
            <Banner tone="warning" role="alert">
              {t("assistant.turnActive")}
            </Banner>
          ) : (
            <ActionError
              error={error}
              translated={apiErrorView(error, t)}
              title={t("assistant.sendFailed")}
              onDismiss={() => {
                focusComposer();
                setError(null);
              }}
            />
          )
        ) : null}
        {attachments.rejections.length > 0 ? (
          <Banner tone="warning" role="alert">
            {attachments.rejections.map((r) => (
              <p key={r}>{r}</p>
            ))}
          </Banner>
        ) : null}
        <Composer
          id={composerId}
          inputRef={composerRef}
          label={t("assistant.composer.label")}
          placeholder={placeholder ?? t("assistant.composer.placeholder")}
          hint={t("assistant.composer.hint")}
          value={text}
          onChange={setText}
          onSubmit={submit}
          maxChars={MAX_CHARS}
          inputClassName="textarea assistant-input"
          attachments={attachments.items}
          onAddFiles={attachments.add}
          onRemoveAttachment={attachments.remove}
          longPaste
          slashCommands={ALL_SLASH_COMMANDS}
          onSlashCommand={runCommand}
          dictation
          counterTestId="assistant-counter"
          controls={controls}
          above={
            panel?.kind === "help" ? (
              <HelpPanel commands={ALL_SLASH_COMMANDS} onClose={closePanel} />
            ) : panel?.kind === "mcp" ? (
              <McpPanel onClose={closePanel} />
            ) : panel?.kind === "note" ? (
              <NotePanel text={panel.text} onClose={closePanel} />
            ) : null
          }
          actions={
            (pending && waiting) || looping ? (
              <Button onClick={() => void stop()} disabled={pending?.stopping === true} data-testid="assistant-stop">
                {pending?.stopping ? t("assistant.stopping") : t("assistant.stop")}
              </Button>
            ) : (
              <IconButton type="submit" variant="primary" label={t("assistant.send")} disabled={!canSend}>
                <SendIcon />
              </IconButton>
            )
          }
        />
      </form>
    </div>
  );
}
